import React, { useEffect, useState } from "react";
import { api_Routes } from "../../../tools/api_Routes";
import { Helper } from "../../../tools/Helper";
import {
    LinearProgress,
    TablePagination,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Button,
    Menu,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Box,
    Chip,
} from "@mui/material";
import DataTable from "./data-table";
import { AddButton } from "../../Button/AddButton";
import { Can } from "../../Can";
import { useNavigate } from "react-router-dom";
import TemplateDialog from "../TemplateDialog";

export const UserCampaingsTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [allData, setAllData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selected, setSelected] = useState(new Set());
    const [selectAllMode, setSelectAllMode] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [filters, setFilters] = useState({
        status: "all",
        verification: "all",
        groups: "all",
    });
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [templateType, setTemplateType] = useState('rich_text');

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const handleKeywordChange = (event) => {
        setKeyword(event.target.value);
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setCurrentPage(0);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedKeyword(keyword);
        }, 300);
        return () => {
            clearTimeout(handler);
        };
    }, [keyword]);

    const getData = async () => {
        setIsLoading(true);
        const queryParams = [];

        if (debouncedKeyword) queryParams.push(`name=${debouncedKeyword}`);
        if (filters.status !== 'all') queryParams.push(`status=${filters.status}`);


        const { response, message } = await Helper.Get({
            url: `${api_Routes.Users.UserCampaignsView}?per_page=${rowsPerPage}&page=${
                currentPage + 1
            }${queryParams?.length > 0 ? `&${queryParams.join('&')}` : ""}`,
            hasToken: true,
        });

        if (response) {
            setData(response);
        }
        setIsLoading(false);
    };

    const handleSelectionChange = (newSelected, newSelectAllMode) => {
        setSelected(newSelected);
        setSelectAllMode(newSelectAllMode);
    };

    const handleTemplateSelect = (template) => {
        setTemplateDialogOpen(false);

        const route = template.type === 'rich_text'
            ? '/email-builder'
            : '/email-template';

        navigate(route, {
            state: {
                selectedIds: Array.from(selected),
                selectAllMode,
                filters,
                template
            }
        });
    };

    const handleCreateNewTemplate = () => {
        setTemplateDialogOpen(false);

        const route = templateType === 'rich_text'
            ? '/email-builder'
            : '/email-template';

        navigate(route, {
            state: {
                selectedIds: Array.from(selected),
                selectAllMode,
                filters
            }
        });
    };

    const handleBulkAction = async (action) => {
        if (action === 'send_email') {
            setTemplateType('rich_text');
            setSelectedAction(action);
            setTemplateDialogOpen(true);
            return;
        }
        if (action === 'send_email_template') {
            setTemplateType('structured');
            setSelectedAction(action);
            setTemplateDialogOpen(true);
            return;
        }
        if (action === 'send_ticket') {
            navigate('/ticket-builder', {
                state: {
                    selectedIds: Array.from(selected),
                    selectAllMode,
                    filters
                }
            });
            return;
        }
        setIsLoading(true);
        try {
            const ids = selectAllMode
                ? 'ALL'
                : Array.from(selected);

            const payload = {
                action,
                ids,
                filters: selectAllMode ? filters : null
            };

            const { response } = await Helper.Post({
                url: api_Routes.Users.BulkActions,
                data: payload,
                hasToken: true
            });

            await getData();
            setSelected(new Set());
            setSelectAllMode(false);
        } catch (error) {
            console.error("Bulk action failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [debouncedKeyword, currentPage, rowsPerPage, filters]);

    return (
        <div>
            <div className="mb-4">
                <div className="flex flex-wrap gap-4 items-center mb-3">
                    <label className="font-bold inline-block text-textColor dark:text-darkTextColor text-[15px] text-start">
                        Search :
                        <input
                            placeholder="Write any keyword"
                            type="text"
                            name="keyword"
                            value={keyword}
                            onChange={handleKeywordChange}
                            autoComplete="off"
                            className="duration-200 font-normal bg-secondary dark:bg-darkPrimary text-textColor dark:text-darkTextColor ml-2
                    border-2 input-box rounded-[0.25rem] py-1 px-3 outline-none"
                        />
                    </label>

                    <FormControl size="small" className="min-w-[120px]">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={filters.status}
                            label="Status"
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="queued">Queued</MenuItem>
                            <MenuItem value="sending">Sending</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>



                    <div className="flex gap-2">
                        {filters.status !== 'all' && (
                            <Chip
                                label={`Status: ${filters.status}`}
                                onDelete={() => handleFilterChange('status', 'all')}
                            />
                        )}
                        {filters.verification !== 'all' && (
                            <Chip
                                label={`Verification: ${filters.verification}`}
                                onDelete={() => handleFilterChange('verification', 'all')}
                            />
                        )}
                        {filters.groups !== 'all' && (
                            <Chip
                                label={`Group: ${filters.groups}`}
                                onDelete={() => handleFilterChange('groups', 'all')}
                            />
                        )}
                    </div>

                    {/*<div className="ml-auto">*/}
                    {/*    <Can I="store" a="users">*/}
                    {/*        <AddButton to="/Users/Add" label="Add User" />*/}
                    {/*    </Can>*/}
                    {/*</div>*/}
                </div>
            </div>

            {(selected.size > 0 || selectAllMode) && (
                <div className="bg-gray-100 p-3 mb-3 rounded flex items-center">
          <span className="mr-4">
            {selectAllMode
                ? `All ${data?.pagination?.total || 0} selected (filtered)`
                : `${selected.size} selected`}
          </span>

                    <div className="relative">
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            endIcon={<span className="ml-1">▼</span>}
                            className="mr-2"
                        >
                            Bulk Actions
                        </Button>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                        >
                            <MenuItem onClick={() => {
                                handleBulkAction('activate');
                                setAnchorEl(null);
                            }}>
                                Activate Selected
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleBulkAction('deactivate');
                                setAnchorEl(null);
                            }}>
                                Deactivate Selected
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleBulkAction('verify_email');
                                setAnchorEl(null);
                            }}>
                                Verify Email
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleBulkAction('unverify_email');
                                setAnchorEl(null);
                            }}>
                                Unverify Email
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleBulkAction('send_email');
                                setAnchorEl(null);
                            }}>
                                Send Email
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleBulkAction('send_email_template');
                                setAnchorEl(null);
                            }}>
                                Send Email with template
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleBulkAction('send_ticket');
                                setAnchorEl(null);
                            }}>
                                Send Ticket
                            </MenuItem>
                        </Menu>
                    </div>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            setSelected(new Set());
                            setSelectAllMode(false);
                        }}
                    >
                        Clear
                    </Button>
                </div>
            )}

            {isLoading ? (
                <LinearProgress sx={{ marginTop: 1 }} />
            ) : data && data?.data?.length > 0 ? (
                <>
                    <div className="overflow-x-auto">
                        <DataTable
                            data={data?.data}
                            allData={allData}
                            selected={selected}
                            selectAllMode={selectAllMode}
                            onSelectionChange={handleSelectionChange}
                            totalCount={data?.pagination?.total}
                            page={currentPage}
                            rowsPerPage={rowsPerPage}
                        />
                    </div>
                    <div className="mt-4 flex justify-center items-center">
                        <TablePagination
                            page={currentPage}
                            className="mt-auto"
                            onPageChange={handlePageChange}
                            count={data.pagination.total}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            variant="outlined"
                            shape="rounded"
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </div>
                </>
            ) : (
                <TableContainer>
                    <Table aria-label="collapsible table">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        backgroundColor: "primary.main",
                                        color: "white",
                                        fontWeight: "bold",
                                    }}
                                >
                                    There are no data yet
                                </TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </TableContainer>
            )}

            <TemplateDialog
                open={templateDialogOpen}
                type={templateType}
                onClose={() => setTemplateDialogOpen(false)}
                onSelect={handleTemplateSelect}
                onCreateNew={handleCreateNewTemplate}
            />
        </div>
    );
};